import React from 'react';
import { createDefaultPanelSessionState } from '../../shared/panels/panelSessionState';
import type { PanelAttachment } from '../../shared/panels/panelSessionState';
import SessionPanel, { type SessionPanelRenderContext } from '../../shared/panels/SessionPanel';
import {
    activateFacilitySlideoutPanelSession,
    closeFacilitySlideoutPanelSession,
    shouldRestoreFacilitySlideoutPanelOpen,
    type FacilitySlideoutPanelId,
} from '../helpers/facilitySlideoutPanelSession';

interface FacilitySlideoutSessionPanelProps {
    attachedClassName: string;
    cancelSelector?: string;
    children: (context: SessionPanelRenderContext) => React.ReactNode;
    className: string;
    elementId: string;
    floatingClassName?: string;
    isOpen: boolean;
    keepMounted?: boolean;
    onAttachmentChange?: (attachment: PanelAttachment) => void;
    onOpenChange: (open: boolean) => void;
    panelId: FacilitySlideoutPanelId;
    panelRef?: React.RefObject<HTMLDivElement>;
    pinnedWidth: number;
}

const DEFAULT_CANCEL_SELECTOR = [
    '.session-panel-toolbar-item',
    '.asset-tag-tool-bar-item',
    '.kb-tool-bar-item',
    'input',
    'button',
    'textarea',
    'select',
    '.e-control',
    '.e-popup',
    '.e-dlg-container',
    '.e-contextmenu-wrapper',
].join(', ');

const FacilitySlideoutSessionPanel = (props: FacilitySlideoutSessionPanelProps) => {
    const panelDefaults = React.useMemo(() => createDefaultPanelSessionState({
        isOpen: props.isOpen,
        panelId: props.panelId,
        pinnedWidth: props.pinnedWidth,
    }), [props.isOpen, props.panelId, props.pinnedWidth]);

    const handleOpenChange = React.useCallback((open: boolean) => {
        if (open) {
            activateFacilitySlideoutPanelSession(props.panelId);
        } else {
            closeFacilitySlideoutPanelSession(props.panelId);
        }

        props.onOpenChange(open);
    }, [props.onOpenChange, props.panelId]);

    const handleAttachmentChange = React.useCallback((attachment: PanelAttachment) => {
        activateFacilitySlideoutPanelSession(props.panelId, undefined, attachment);
        props.onAttachmentChange?.(attachment);
        props.onOpenChange(true);
    }, [props.onAttachmentChange, props.onOpenChange, props.panelId]);

    const shouldRestoreOpen = React.useCallback(() => {
        return shouldRestoreFacilitySlideoutPanelOpen(props.panelId);
    }, [props.panelId]);

    return (
        <SessionPanel
            attachedClassName={props.attachedClassName}
            cancelSelector={props.cancelSelector ?? DEFAULT_CANCEL_SELECTOR}
            className={props.className}
            defaults={panelDefaults}
            elementId={props.elementId}
            floatingClassName={props.floatingClassName ?? 'show floating-position'}
            isOpen={props.isOpen}
            keepMounted={props.keepMounted}
            onOpenChange={handleOpenChange}
            onAttachmentChange={handleAttachmentChange}
            panelId={props.panelId}
            panelRef={props.panelRef}
            shouldRestoreOpen={shouldRestoreOpen}
        >
            {props.children}
        </SessionPanel>
    );
};

export default FacilitySlideoutSessionPanel;
